import { useState, useEffect } from 'react';
import StoreButtons from '../components/StoreButtons';

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.fade-up');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function EmailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

const supportFAQ = [
  {
    q: 'Is Klar really free?',
    a: 'Yes. Klar (Klaro Money) is free on both the App Store and Google Play — no ads, no subscription, no premium tier.',
  },
  {
    q: 'Which devices is it on?',
    a: 'iPhone, iPad, and Mac via the App Store, and Android phones via Google Play. Search "Klaro Money" or use the download buttons above.',
  },
  {
    q: 'Found a bug or have feedback?',
    a: 'Use the form here or email Affan directly. We read every message and ship fixes and features fast.',
  },
  {
    q: 'How do I delete my data?',
    a: 'Your data stays on your device — uninstalling the app removes it. For anything else, email us and we\'ll action it within a few days.',
  },
];

export default function ContactPage() {
  useScrollReveal();

  const [formState, setFormState] = useState({ name: '', college: '', email: '', year: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => setFormState(s => ({ ...s, [e.target.name]: e.target.value }));

  const handleSubmit = e => {
    e.preventDefault();
    const subject = encodeURIComponent(`Klar — message from ${formState.name}`);
    const body = encodeURIComponent(
      `Hi Affan,\n\nName: ${formState.name}\nCollege: ${formState.college}\nYear: ${formState.year}\nEmail: ${formState.email}\n\n${formState.message ? formState.message : ''}`
    );
    window.location.href = `mailto:affan@equios.co?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="contact-hero">
        <div className="inner-hero-bg"/>
        <div className="container">
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <div className="section-label fade-up">Get the App</div>
            <h1 className="text-display fade-up delay-1">
              Download <span className="text-gradient">Klar.</span>
            </h1>
            <p className="text-body-lg mt-4 fade-up delay-2" style={{ maxWidth: 480, margin: '16px auto 24px' }}>
              Klar (Klaro Money) is live and free on the App Store and Google Play. Grab it on your phone — or send us a message below.
            </p>
            <div className="fade-up delay-3" style={{ display: 'flex', justifyContent: 'center' }}>
              <StoreButtons className="center" size="lg" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact grid ─────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Info card */}
            <div className="fade-up">
              <div className="contact-info-card">
                <div className="contact-info-header">
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--lime)', marginBottom: 4 }}>Built by</div>
                  <div style={{ fontSize: 'var(--text-xl)', fontWeight: 800, color: 'var(--text-primary)' }}>Affan Ahmed</div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 4 }}>Founder, Equios</div>
                </div>
                <div className="contact-info-rows">
                  <div className="contact-info-row">
                    <div className="contact-info-icon"><EmailIcon /></div>
                    <div>
                      <div className="contact-info-label">Email</div>
                      <div className="contact-info-value">
                        <a href="mailto:affan@equios.co">affan@equios.co</a>
                      </div>
                    </div>
                  </div>
                  <div className="contact-info-row">
                    <div className="contact-info-icon"><LocationIcon /></div>
                    <div>
                      <div className="contact-info-label">Based in</div>
                      <div className="contact-info-value">India</div>
                    </div>
                  </div>
                  <div className="contact-info-row">
                    <div className="contact-info-icon"><PersonIcon /></div>
                    <div>
                      <div className="contact-info-label">Availability</div>
                      <div className="contact-info-value">
                        <span style={{ color: 'var(--lime)', fontWeight: 700 }}>Live</span>
                        {' '}— App Store &amp; Google Play
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Support FAQ */}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>
                  Quick answers
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {supportFAQ.map(item => (
                    <div key={item.q} style={{ background: 'var(--card-elevated)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '14px 16px' }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>{item.q}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-relaxed)' }}>{item.a}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="fade-up delay-2">
              {submitted ? (
                <div className="form-card" style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
                  <h3 style={{ fontSize: 'var(--text-2xl)', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
                    Email draft opened!
                  </h3>
                  <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)', lineHeight: 'var(--leading-relaxed)', marginBottom: 24 }}>
                    Your default email app should have opened with a pre-filled message. Send it and Affan will get back to you within 2–3 days.
                  </p>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setSubmitted(false)}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <div className="form-card">
                  <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 8 }}>
                    Send us a message
                  </h2>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 28 }}>
                    Feedback, bugs, or just saying hi — we read every message.
                  </p>

                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 0 }}>
                      <div className="form-group">
                        <label className="form-label">
                          Full Name <span className="form-required">*</span>
                        </label>
                        <input
                          className="form-input"
                          type="text"
                          name="name"
                          placeholder="Priya Raghavan"
                          value={formState.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Year of Study <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span>
                        </label>
                        <select className="form-select" name="year" value={formState.year} onChange={handleChange}>
                          <option value="">Select year</option>
                          <option>1st Year</option>
                          <option>2nd Year</option>
                          <option>3rd Year</option>
                          <option>4th Year</option>
                          <option>Postgraduate</option>
                          <option>PhD</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        College / University <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span>
                      </label>
                      <input
                        className="form-input"
                        type="text"
                        name="college"
                        placeholder="IIT Madras, Pune University, etc."
                        value={formState.college}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Email Address <span className="form-required">*</span>
                      </label>
                      <input
                        className="form-input"
                        type="email"
                        name="email"
                        placeholder="you@example.com"
                        value={formState.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Anything else? <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(optional)</span>
                      </label>
                      <textarea
                        className="form-textarea"
                        name="message"
                        placeholder="How you heard about Klar, what you want to track, any questions..."
                        value={formState.message}
                        onChange={handleChange}
                        rows={4}
                      />
                    </div>

                    <button type="submit" className="btn btn-primary btn-lg w-full" style={{ justifyContent: 'center', marginTop: 8 }}>
                      Send Message <ArrowRight />
                    </button>

                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', textAlign: 'center', marginTop: 12 }}>
                      This opens your email app with a pre-filled message. No data is sent to any server.
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom note ──────────────────────────────────────────────────── */}
      <section className="section-sm" style={{ background: 'var(--bg-alt)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
            <div style={{ fontSize: '1.75rem', marginBottom: 12 }}>📬</div>
            <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
              Prefer to email directly?
            </h3>
            <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)', marginBottom: 20 }}>
              Drop a message to <a href="mailto:affan@equios.co" style={{ color: 'var(--lime)' }}>affan@equios.co</a> with whatever's on your mind. We read every message and reply personally.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
